import React, { useState, useEffect, useMemo } from 'react';
import api from '../config';
import {
  Calculator, Loader2, ChevronDown, ChevronRight, TrendingUp, Percent,
  Landmark, ArrowRightLeft, Scale,
} from 'lucide-react';

/**
 * LBO modeling panel for a deal: assumptions editor, returns dashboard,
 * case comparison, sensitivity grid, and Analysis at Various Prices (AVP).
 * Every figure is computed server-side strictly from the user's assumptions
 * — nothing is estimated or invented. Assumptions prefill from the deal's
 * intake financials when no saved model exists.
 */

const fmtUSD = (v) => (v == null || v === '' ? '—' : `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 1 })}`);
const fmtNum = (v, d = 1) => (v == null || v === '' ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: d }));
const pct = (v) => (v == null || v === '' ? '—' : `${Number(v).toFixed(1)}%`);
const xf = (v) => (v == null || v === '' ? '—' : `${Number(v).toFixed(1)}x`);
const csv = (s) => (s || '').split(',').map(x => parseFloat(x.trim())).filter(x => !Number.isNaN(x));

const SEN_FIELDS = [
  { id: 'entry_multiple', label: 'Entry multiple (x EBITDA)' },
  { id: 'purchase_price', label: 'Purchase price ($)' },
  { id: 'revenue_growth_pct', label: 'Revenue growth (%)' },
  { id: 'ebitda_margin_pct', label: 'EBITDA margin (%)' },
  { id: 'exit_multiple', label: 'Exit multiple (x EBITDA)' },
];

function defaultAssumptions(deal) {
  const rev = deal?.annualRevenue || deal?.financial_overview?.annual_revenue || '';
  const margin = deal?.ebitdaMargin ?? deal?.financial_overview?.ebitda_margin_pct ?? '';
  const val = deal?.targetValuation || deal?.financial_overview?.target_valuation || '';
  const ebitda = (rev && margin) ? (rev * margin / 100) : '';
  const mult = (val && ebitda) ? +(val / ebitda).toFixed(1) : '';
  return {
    entry: {
      purchase_price: val || '',
      entry_ebitda: ebitda || '',
      entry_multiple: mult || '',
      transaction_fees_pct: 1,
      financing_fees_pct: 2,
    },
    debt: [{ name: 'Senior debt', amount: '', pct_of_price: 50, interest_rate_pct: 9, sweep_pct: 75 }],
    operating: {
      revenue: rev,
      ebitda_margin_pct: margin,
      revenue_growth_pct: '',
      revenue_growth_path_pct: [],
      da_pct_of_revenue: 3,
      capex_pct_of_revenue: 2,
      nwc_pct_of_revenue: 8,
      cash_tax_rate_pct: 25,
    },
    exit: { hold_years: 5, exit_multiple: '' },
  };
}

/* ── small field primitives ─────────────────────────────────────────── */
function NumField({ label, value, onChange, placeholder, hint, req, missing, pref, id }) {
  const empty = value === '' || value == null;
  return (
    <label className="block" id={id}>
      <span className="text-xs font-medium text-slate-500">
        {label}
        {req && <span className="text-red-500 ml-0.5">*</span>}
        {pref && (
          <span
            className="ml-1.5 inline-block whitespace-nowrap text-[9px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-px align-middle"
            title="Prefilled from this deal's intake details — edit freely"
          >from deal</span>
        )}
      </span>
      <input
        type="number" step="any" value={value ?? ''} placeholder={placeholder}
        onChange={e => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
        className={`form-input mt-0.5 ${missing && empty ? 'border-red-400 bg-red-50/60' : ''}`}
      />
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

function Section({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Icon className="w-4 h-4 text-slate-500" />{title}</span>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="p-4">{children}</div>}
    </div>
  );
}

export default function LboModel({ dealId, deal }) {
  const [loading, setLoading] = useState(true);
  const [a, setA] = useState(null);          // assumptions draft
  const [scenarios, setScenarios] = useState({}); // override drafts
  const [run, setRun] = useState(null);      // base run result
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const [compare, setCompare] = useState(null);
  const [senRow, setSenRow] = useState('revenue_growth_pct');
  const [senRowVals, setSenRowVals] = useState('2, 6, 10');
  const [senCol, setSenCol] = useState('exit_multiple');
  const [senColVals, setSenColVals] = useState('8, 9, 10');
  const [senGrid, setSenGrid] = useState(null);
  const [avpMults, setAvpMults] = useState('');
  const [avp, setAvp] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/api/dd/deals/${dealId}/lbo`)
      .then(res => {
        if (cancelled) return;
        if (res.data && res.data.assumptions) {
          setA(res.data.assumptions);
          setScenarios(res.data.scenarios || {});
          setSavedAt(true);
        } else {
          setA(defaultAssumptions(deal));
        }
      })
      .catch(() => { if (!cancelled) setA(defaultAssumptions(deal)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dealId, deal]);

  const upd = (section, key, val) => setA(p => ({ ...p, [section]: { ...p[section], [key]: val } }));
  const updTranche = (i, key, val) => setA(p => {
    const debt = p.debt.map((t, idx) => idx === i ? { ...t, [key]: val } : t);
    return { ...p, debt };
  });

  const saveModel = async (extraScenarios = scenarios) => {
    await api.post(`/api/dd/deals/${dealId}/lbo`, {
      assumptions: a,
      scenarios: extraScenarios,
    });
    setSavedAt(true);
  };

  const runModel = async () => {
    if (missing.length) {
      setShowMissing(true);
      setErr(
        `${missing.length} required input${missing.length > 1 ? 's' : ''} still missing: ` +
        `${missing.map(m => m.label).join(', ')}. Missing fields are highlighted below (marked *).`
      );
      const el = document.getElementById(missing[0].id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setBusy('run'); setErr(''); setRun(null);
    try {
      await saveModel();
      const res = await api.post(`/api/dd/deals/${dealId}/lbo/scenarios/management/run`);
      setRun(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Model could not run — check that every required assumption is filled in.');
    } finally { setBusy(''); }
  };

  const runCompare = async () => {
    setBusy('cmp'); setErr('');
    try {
      await saveModel();
      const res = await api.post(`/api/dd/deals/${dealId}/lbo/compare`);
      setCompare(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Comparison failed.');
    } finally { setBusy(''); }
  };

  const runSensitivity = async () => {
    setBusy('sen'); setErr(''); setSenGrid(null);
    try {
      await saveModel();
      const res = await api.post(`/api/dd/deals/${dealId}/lbo/sensitivity`, {
        row_field: senRow, row_values: csv(senRowVals),
        col_field: senCol, col_values: csv(senColVals),
      });
      setSenGrid(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Sensitivity failed — enter comma-separated numbers for both dimensions.');
    } finally { setBusy(''); }
  };

  const runAvp = async () => {
    setBusy('avp'); setErr(''); setAvp(null);
    try {
      await saveModel();
      const body = csv(avpMults).length ? { entry_multiples: csv(avpMults) } : {};
      const res = await api.post(`/api/dd/deals/${dealId}/lbo/avp`, body);
      setAvp(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'AVP analysis failed.');
    } finally { setBusy(''); }
  };

  const years = run?.operating_years || [];
  const su = run?.sources_uses;

  const scenUpd = (name, path, v) => setScenarios(p => {
    const s = { ...(p[name] || {}) };
    const [sec, key] = path.split('.');
    s[sec] = { ...(s[sec] || {}), [key]: v };
    return { ...p, [name]: s };
  });

  // Whether the model is complete enough to run (mirrors server validation).
  const [showMissing, setShowMissing] = useState(false);
  const isNum = v => v !== '' && v != null && !Number.isNaN(Number(v));

  // Deal intake numbers used for prefill chips (chip hides once edited away).
  const dealRev = deal?.annualRevenue ?? deal?.financial_overview?.annual_revenue ?? '';
  const dealMargin = deal?.ebitdaMargin ?? deal?.financial_overview?.ebitda_margin_pct ?? '';
  const dealVal = deal?.targetValuation ?? deal?.financial_overview?.target_valuation ?? '';
  const dealEbitda = (dealRev !== '' && dealMargin !== '') ? dealRev * dealMargin / 100 : '';
  const dealMult = (dealVal !== '' && dealEbitda !== '') ? +(dealVal / dealEbitda).toFixed(1) : '';
  const wasPref = (v, dealV) =>
    dealV !== '' && dealV != null && isNum(v) && Number(v) === Number(dealV);

  if (loading || !a) {
    return (
      <div className="card p-6 flex items-center justify-center gap-2 text-sm text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading LBO model…
      </div>
    );
  }

  // ── Required-input validation (mirrors the server's checks) ──────
  const entryOk = isNum(a.entry.purchase_price)
    || (isNum(a.entry.entry_ebitda) && isNum(a.entry.entry_multiple));
  const growthOk = isNum(a.operating.revenue_growth_pct)
    || (a.operating.revenue_growth_path_pct || []).length > 0;
  const trancheOk = t => (isNum(t.amount) || isNum(t.pct_of_price)) && isNum(t.interest_rate_pct);
  const REQ = [
    { ok: entryOk, label: 'Purchase price (or Entry EBITDA + Entry multiple)', id: 'lbo-f-price' },
    { ok: isNum(a.entry.transaction_fees_pct), label: 'Transaction fees %', id: 'lbo-f-txfees' },
    { ok: isNum(a.entry.financing_fees_pct), label: 'Financing fees %', id: 'lbo-f-finfees' },
    { ok: (a.debt || []).length > 0 && a.debt.every(trancheOk),
      label: 'Debt tranche amount (or % of price) + interest rate', id: 'lbo-f-debt' },
    { ok: isNum(a.operating.revenue), label: 'Revenue, year 1', id: 'lbo-f-rev' },
    { ok: isNum(a.operating.ebitda_margin_pct), label: 'EBITDA margin %', id: 'lbo-f-margin' },
    { ok: growthOk, label: 'Revenue growth (flat % or per-year path)', id: 'lbo-f-growth' },
    { ok: isNum(a.operating.da_pct_of_revenue), label: 'D&A (% of revenue)', id: 'lbo-f-da' },
    { ok: isNum(a.operating.capex_pct_of_revenue), label: 'Capex (% of revenue)', id: 'lbo-f-capex' },
    { ok: isNum(a.operating.nwc_pct_of_revenue), label: 'NWC (% of revenue)', id: 'lbo-f-nwc' },
    { ok: isNum(a.operating.cash_tax_rate_pct), label: 'Cash tax rate %', id: 'lbo-f-tax' },
    { ok: isNum(a.exit.hold_years), label: 'Hold years', id: 'lbo-f-hold' },
  ];
  const missing = REQ.filter(r => !r.ok);
  const miss = (id) => showMissing && !REQ.find(r => r.id === id)?.ok;

  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-slate-500" />
          <h3 className="text-base font-semibold text-slate-900">LBO Model</h3>
          {savedAt && <span className="text-[11px] text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Saved</span>}
          {missing.length > 0 ? (
            <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
              {missing.length} required input{missing.length > 1 ? 's' : ''} missing
            </span>
          ) : (
            <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
              All required inputs filled
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={runModel} disabled={!!busy} className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1.5">
            {busy === 'run' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />}
            {busy === 'run' ? 'Running…' : 'Save & Run Model'}
          </button>
        </div>
      </div>
      <p className="text-xs text-slate-500">
        Every figure below is computed from your assumptions only — nothing is estimated or invented.
        Fields marked <span className="text-red-500 font-semibold">*</span> are required; fields tagged{' '}
        <span className="inline-block whitespace-nowrap text-[9px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-px align-middle">from deal</span>{' '}
        were prefilled from the deal&rsquo;s intake details.
      </p>
      {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}

      {/* ── Returns dashboard ─────────────────────────────────────────── */}
      {run && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { l: 'MOIC', v: xf(run.returns?.moic), neg: (run.returns?.moic ?? 0) < 0 },
              { l: 'IRR', v: run.returns?.irr_pct == null ? 'n/a' : pct(run.returns?.irr_pct) },
              { l: 'Equity invested', v: fmtUSD(run.returns?.invested_equity) },
              { l: 'Exit equity', v: fmtUSD(run.exit?.exit_equity), neg: (run.exit?.exit_equity ?? 0) < 0 },
              { l: 'Revenue CAGR', v: pct(run.cagr?.revenue_cagr_pct) },
              { l: 'EBITDA CAGR', v: pct(run.cagr?.ebitda_cagr_pct) },
            ].map(({ l, v, neg }) => (
              <div key={l} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                <div className="text-[11px] font-medium text-slate-500">{l}</div>
                <div className={`text-sm font-semibold mt-0.5 ${neg ? 'text-rose-600' : 'text-slate-900'}`}>{v}</div>
              </div>
            ))}
          </div>
          {run.returns?.irr_pct == null && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              IRR is <b>not meaningful</b> for these assumptions, so it reads <b>n/a</b>. At an exit multiple of{' '}
              <b>{run.exit?.exit_multiple}x</b> the exit value ({fmtUSD(run.exit?.exit_ev)}) is below the debt still
              outstanding at exit ({fmtUSD(run.exit?.debt_at_exit)}), so exit equity is negative. Raise the exit
              multiple, lower leverage, or lift EBITDA growth to produce a return.
            </div>
          )}
        </div>
      )}

      {run && su && (
        <div className="text-xs text-slate-600 border border-slate-200 rounded-lg px-3 py-2 bg-white">
          <span className="font-medium">Sources &amp; Uses:</span>{' '}
          Purchase {fmtUSD(su.purchase_price)} ({su.basis}) · Debt {fmtUSD(su.total_debt)} ({(su.debt_pct_of_uses * 100).toFixed(0)}% of uses) ·
          Equity {fmtUSD(su.equity)} · Fees {fmtUSD(su.transaction_fees + su.financing_fees)} ·
          Exit at {xf(run.exit?.exit_multiple)} × {fmtUSD(run.exit?.exit_ebitda)} EBITDA
        </div>
      )}

      {/* ── Assumptions ───────────────────────────────────────────────── */}
      <Section title="Entry & Financing" icon={Landmark}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <NumField id="lbo-f-price" req missing={miss('lbo-f-price')}
            pref={wasPref(a.entry.purchase_price, dealVal)}
            label="Purchase price ($)" value={a.entry.purchase_price}
            onChange={v => upd('entry', 'purchase_price', v)} placeholder="or use entry multiple" />
          <NumField req missing={miss('lbo-f-price')}
            pref={wasPref(a.entry.entry_ebitda, dealEbitda)}
            label="Entry EBITDA ($)" value={a.entry.entry_ebitda}
            onChange={v => upd('entry', 'entry_ebitda', v)} hint="needed with entry multiple" />
          <NumField req missing={miss('lbo-f-price')}
            pref={wasPref(a.entry.entry_multiple, dealMult)}
            label="Entry multiple (x EBITDA)" value={a.entry.entry_multiple}
            onChange={v => upd('entry', 'entry_multiple', v)} hint="used if no purchase price" />
          <NumField id="lbo-f-txfees" req missing={miss('lbo-f-txfees')}
            label="Transaction fees (% of price)" value={a.entry.transaction_fees_pct}
            onChange={v => upd('entry', 'transaction_fees_pct', v)} />
          <NumField id="lbo-f-finfees" req missing={miss('lbo-f-finfees')}
            label="Financing fees (% of debt)" value={a.entry.financing_fees_pct}
            onChange={v => upd('entry', 'financing_fees_pct', v)} />
        </div>
        <p className="text-[11px] text-slate-400 mt-2">
          Required: a purchase price, or entry EBITDA + entry multiple together.
        </p>
        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-600 mb-2">Debt tranches</div>
          <div className="space-y-2">
            {(a.debt || []).map((t, i) => (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                <label className="block">
                  <span className="text-[11px] text-slate-500">Name</span>
                  <input value={t.name || ''} onChange={e => updTranche(i, 'name', e.target.value)} className="form-input mt-0.5" />
                </label>
                <NumField req missing={miss('lbo-f-debt')} label="Amount ($)" value={t.amount} onChange={v => updTranche(i, 'amount', v)} placeholder="or % below" />
                <NumField req missing={miss('lbo-f-debt')} label="% of price" value={t.pct_of_price} onChange={v => updTranche(i, 'pct_of_price', v)} />
                <NumField req missing={miss('lbo-f-debt')} label="Interest rate %" value={t.interest_rate_pct} onChange={v => updTranche(i, 'interest_rate_pct', v)} />
                <div className="flex items-end gap-2">
                  <NumField label="FCF sweep %" value={t.sweep_pct} onChange={v => updTranche(i, 'sweep_pct', v)} />
                  {a.debt.length > 1 && (
                    <button onClick={() => setA(p => ({ ...p, debt: p.debt.filter((_, idx) => idx !== i) }))}
                      className="btn-secondary text-xs py-1.5 px-2 mb-0.5">✕</button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => setA(p => ({ ...p, debt: [...(p.debt || []), { name: '', amount: '', pct_of_price: '', interest_rate_pct: '', sweep_pct: '' }] }))}
            className="btn-secondary text-xs py-1.5 px-3 mt-2">+ Add tranche</button>
        </div>
      </Section>

      <Section title="Operating Assumptions" icon={Percent}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <NumField id="lbo-f-rev" req missing={miss('lbo-f-rev')}
            pref={wasPref(a.operating.revenue, dealRev)}
            label="Revenue, year 1 ($)" value={a.operating.revenue}
            onChange={v => upd('operating', 'revenue', v)} />
          <NumField id="lbo-f-margin" req missing={miss('lbo-f-margin')}
            pref={wasPref(a.operating.ebitda_margin_pct, dealMargin)}
            label="EBITDA margin %" value={a.operating.ebitda_margin_pct}
            onChange={v => upd('operating', 'ebitda_margin_pct', v)} />
          <NumField id="lbo-f-growth" req missing={miss('lbo-f-growth')}
            label="Revenue growth % / yr" value={a.operating.revenue_growth_pct}
            onChange={v => upd('operating', 'revenue_growth_pct', v)}
            hint="required — or use the per-year path below" />
          <NumField req missing={miss('lbo-f-hold')}
            label="Hold years" value={a.exit.hold_years} onChange={v => upd('exit', 'hold_years', v)} />
          <NumField id="lbo-f-da" req missing={miss('lbo-f-da')}
            label="D&A (% of revenue)" value={a.operating.da_pct_of_revenue}
            onChange={v => upd('operating', 'da_pct_of_revenue', v)} />
          <NumField id="lbo-f-capex" req missing={miss('lbo-f-capex')}
            label="Capex (% of revenue)" value={a.operating.capex_pct_of_revenue}
            onChange={v => upd('operating', 'capex_pct_of_revenue', v)} />
          <NumField id="lbo-f-nwc" req missing={miss('lbo-f-nwc')}
            label="NWC (% of revenue)" value={a.operating.nwc_pct_of_revenue}
            onChange={v => upd('operating', 'nwc_pct_of_revenue', v)} />
          <NumField id="lbo-f-tax" req missing={miss('lbo-f-tax')}
            label="Cash tax rate %" value={a.operating.cash_tax_rate_pct}
            onChange={v => upd('operating', 'cash_tax_rate_pct', v)} />
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-500">Revenue growth path (% per year, comma-separated — alternative to the flat growth rate above)</span>
            <input
              value={(a.operating.revenue_growth_path_pct || []).join(', ')}
              onChange={e => upd('operating', 'revenue_growth_path_pct', csv(e.target.value))}
              placeholder="e.g. 10, 8, 6, 4, 2"
              className="form-input mt-0.5"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-500">EBITDA margin path (% per year, comma-separated — overrides flat margin)</span>
            <input
              value={(a.operating.ebitda_margin_path_pct || []).join(', ')}
              onChange={e => upd('operating', 'ebitda_margin_path_pct', csv(e.target.value))}
              placeholder="e.g. 12.5, 13, 13.5, 14, 14"
              className="form-input mt-0.5"
            />
          </label>
        </div>
      </Section>

      <Section title="Exit" icon={ArrowRightLeft} defaultOpen={false}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <NumField id="lbo-f-hold" req missing={miss('lbo-f-hold')}
            label="Hold years" value={a.exit.hold_years} onChange={v => upd('exit', 'hold_years', v)} />
          <NumField label="Exit multiple (x EBITDA)" value={a.exit.exit_multiple}
            onChange={v => upd('exit', 'exit_multiple', v)}
            hint={dealMult !== '' && isNum(dealMult)
              ? `no multiple expansion = ${dealMult}x (the multiple the current ask implies)`
              : 'using the entry multiple means no multiple expansion'} />
        </div>
      </Section>

      {/* ── Yearly projection table ────────────────────────────────────── */}
      {run && years.length > 0 && (
        <Section title="Projection (per year)" icon={Scale} defaultOpen={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-slate-200">
                  {['Year', 'Revenue', 'EBITDA', 'Margin %', 'FCF', 'Interest', 'Taxes', 'Debt (EOY)', 'Cash (EOY)'].map(h => (
                    <th key={h} className="text-left font-medium py-1.5 pr-4 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {years.map(y => (
                  <tr key={y.year} className="border-b border-slate-100 text-slate-700">
                    <td className="py-1.5 pr-4 font-medium">{y.year}</td>
                    <td className="py-1.5 pr-4">{fmtUSD(y.revenue)}</td>
                    <td className="py-1.5 pr-4">{fmtUSD(y.ebitda)}</td>
                    <td className="py-1.5 pr-4">{fmtNum(y.ebitda_margin_pct)}</td>
                    <td className="py-1.5 pr-4">{fmtUSD(y.free_cash_flow)}</td>
                    <td className="py-1.5 pr-4">{fmtUSD(y.interest)}</td>
                    <td className="py-1.5 pr-4">{fmtUSD(y.taxes)}</td>
                    <td className="py-1.5 pr-4">{fmtUSD(y.debt_balance)}</td>
                    <td className="py-1.5 pr-4">{fmtUSD(y.cash)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* ── Case comparison ────────────────────────────────────────────── */}
      <Section title="Case Comparison (scenarios)" icon={ArrowRightLeft} defaultOpen={false}>
        <p className="text-xs text-slate-500 mb-2">
          Downside / Base / Custom override the base assumptions. Leave a field empty to inherit the base case.
        </p>
        {['downside', 'base', 'custom'].map(name => (
          <div key={name} className="mb-3 last:mb-0">
            <div className="text-xs font-semibold text-slate-600 capitalize mb-1">{name}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <NumField label="Revenue growth %" value={scenarios[name]?.operating?.revenue_growth_pct ?? ''}
                onChange={v => scenUpd(name, 'operating.revenue_growth_pct', v)} placeholder="inherit" />
              <NumField label="EBITDA margin %" value={scenarios[name]?.operating?.ebitda_margin_pct ?? ''}
                onChange={v => scenUpd(name, 'operating.ebitda_margin_pct', v)} placeholder="inherit" />
              <NumField label="Exit multiple" value={scenarios[name]?.exit?.exit_multiple ?? ''}
                onChange={v => scenUpd(name, 'exit.exit_multiple', v)} placeholder="inherit" />
              <NumField label="Hold years" value={scenarios[name]?.exit?.hold_years ?? ''}
                onChange={v => scenUpd(name, 'exit.hold_years', v)} placeholder="inherit" />
            </div>
          </div>
        ))}
        <button onClick={runCompare} disabled={!!busy} className="btn-secondary text-xs py-1.5 px-3 mt-2 inline-flex items-center gap-1.5">
          {busy === 'cmp' && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Run & Compare Cases
        </button>
        {compare && (
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-slate-200">
                  {['Case', 'MOIC', 'IRR', 'Equity invested', 'Exit equity', 'Rev CAGR', 'EBITDA CAGR'].map(h => (
                    <th key={h} className="text-left font-medium py-1.5 pr-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(compare.scenarios || []).map(s => (
                  <tr key={s.scenario} className="border-b border-slate-100">
                    <td className="py-1.5 pr-4 font-medium capitalize text-slate-700">{s.scenario}</td>
                    <td className="py-1.5 pr-4">{xf(s.moic)}</td>
                    <td className="py-1.5 pr-4">{pct(s.irr_pct)}</td>
                    <td className="py-1.5 pr-4">{fmtUSD(s.invested_equity)}</td>
                    <td className="py-1.5 pr-4">{fmtUSD(s.exit_equity)}</td>
                    <td className="py-1.5 pr-4">{pct(s.revenue_cagr_pct)}</td>
                    <td className="py-1.5 pr-4">{pct(s.ebitda_cagr_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(compare.scenarios || []).some(s => s.error) && (
              <p className="text-[11px] text-amber-700 mt-1">
                Some cases could not run (missing assumptions). Fill the overrides or the base assumptions and re-run.
              </p>
            )}
          </div>
        )}
      </Section>

      {/* ── Sensitivity ────────────────────────────────────────────────── */}
      <Section title="Sensitivity Grid" icon={Percent} defaultOpen={false}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
          <label className="block">
            <span className="text-[11px] text-slate-500">Rows</span>
            <select value={senRow} onChange={e => setSenRow(e.target.value)} className="form-select mt-0.5 w-full">
              {SEN_FIELDS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] text-slate-500">Row values</span>
            <input value={senRowVals} onChange={e => setSenRowVals(e.target.value)} className="form-input mt-0.5" placeholder="2, 6, 10" />
          </label>
          <label className="block">
            <span className="text-[11px] text-slate-500">Columns</span>
            <select value={senCol} onChange={e => setSenCol(e.target.value)} className="form-select mt-0.5 w-full">
              {SEN_FIELDS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </label>
          <div className="flex items-end gap-2">
            <label className="block flex-1">
              <span className="text-[11px] text-slate-500">Column values</span>
              <input value={senColVals} onChange={e => setSenColVals(e.target.value)} className="form-input mt-0.5" placeholder="8, 9, 10" />
            </label>
            <button onClick={runSensitivity} disabled={!!busy} className="btn-secondary text-xs py-1.5 px-3 mb-0.5 inline-flex items-center gap-1">
              {busy === 'sen' && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Run
            </button>
          </div>
        </div>
        {senGrid && (
          <div className="overflow-x-auto mt-3">
            <table className="text-xs border-collapse">
              <thead>
                <tr>
                  <th className="border border-slate-200 bg-slate-50 px-2 py-1.5 text-slate-500 font-medium">
                    {SEN_FIELDS.find(f => f.id === senGrid.row_field)?.label}
                  </th>
                  {senGrid.col_values.map(cv => (
                    <th key={cv} className="border border-slate-200 bg-slate-50 px-2 py-1.5 text-slate-600 font-medium">{cv}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {senGrid.rows.map(r => (
                  <tr key={r.value}>
                    <td className="border border-slate-200 bg-slate-50 px-2 py-1.5 font-medium text-slate-600">{r.value}</td>
                    {r.cells.map((c, i) => (
                      <td key={i} className={`border border-slate-200 px-2 py-1.5 text-center ${(c.moic ?? 0) < 0 ? 'text-rose-600' : 'text-slate-700'}`}>
                        {c.moic == null
                          ? <span className="text-slate-300">—</span>
                          : c.irr_pct == null
                            ? <span title="IRR is undefined here (negative exit equity) — showing MOIC">{xf(c.moic)}<span className="block text-[10px] text-slate-400">IRR n/a</span></span>
                            : <span>{pct(c.irr_pct)} <span className="text-slate-400">({xf(c.moic)})</span></span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-slate-400 mt-1">Cells show IRR (MOIC). Where IRR is undefined (negative exit equity) the MOIC is shown alone; — = combination not runnable with the current assumptions.</p>
          </div>
        )}
      </Section>

      {/* ── AVP ────────────────────────────────────────────────────────── */}
      <Section title="Analysis at Various Prices (AVP)" icon={Landmark} defaultOpen={false}>
        <div className="flex items-end gap-2 flex-wrap">
          <label className="block flex-1 min-w-64">
            <span className="text-[11px] text-slate-500">Entry multiples to test (comma-separated)</span>
            <input value={avpMults} onChange={e => setAvpMults(e.target.value)} className="form-input mt-0.5"
              placeholder="leave empty for ±2.0x around the base in 0.5x steps" />
          </label>
          <button onClick={runAvp} disabled={!!busy} className="btn-secondary text-xs py-1.5 px-3 inline-flex items-center gap-1">
            {busy === 'avp' && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Run AVP
          </button>
        </div>
        {avp && (
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-slate-200">
                  {(avp.basis === 'entry_multiple'
                    ? ['Entry multiple', 'Purchase price', 'Equity invested', 'Exit equity', 'MOIC', 'IRR']
                    : ['Purchase price', 'Entry multiple (implied)', 'Equity invested', 'Exit equity', 'MOIC', 'IRR']
                  ).map(h => (
                    <th key={h} className="text-left font-medium py-1.5 pr-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {avp.rows.map(r => (
                  <tr key={r.value} className="border-b border-slate-100">
                    <td className="py-1.5 pr-4 font-medium text-slate-700">
                      {avp.basis === 'entry_multiple' ? xf(r.value) : fmtUSD(r.value)}
                    </td>
                    <td className="py-1.5 pr-4">
                      {avp.basis === 'entry_multiple' ? fmtUSD(r.purchase_price) : xf(r.implied_entry_multiple)}
                    </td>
                    <td className="py-1.5 pr-4">{fmtUSD(r.invested_equity)}</td>
                    <td className="py-1.5 pr-4">{fmtUSD(r.exit_equity)}</td>
                    <td className="py-1.5 pr-4">{xf(r.moic)}</td>
                    <td className="py-1.5 pr-4">{pct(r.irr_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-slate-400 mt-1">{avp.note}</p>
          </div>
        )}
      </Section>
    </div>
  );
}
